import { Timestamp } from 'firebase/firestore';
import React, { FC, Fragment, ReactNode, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';

import {
  ErrorBoundary,
  Layout,
  ModalInitialLanguageSetup,
  ModalSelectLanguage,
  OrganizationDisabled,
  TrialExpired,
} from '@/components';
import { useLanguageState, useOrganizationState, useUserState } from '@/state';
import {
  DEFAULT_LOCALE, isSupportedLocale, LocaleNavigate, SUPPORTED_LOCALES, useLocaleNavigate,
} from '@/utils/locale';

import { DashboardLoadingIcon } from '../../components/Loaders';
import {
  getBreakfastOrdersEntry,
  getTranslatedExternalAppsEntries,
  getTranslatedMenuConfig,
  getTranslatedMuseumEntries,
  getTranslatedOperaEntries,
} from '../../config/menuConfig';
import { useHeroToast } from '../../hooks';
import { useNotificationAlerts } from '../../hooks/useNotificationAlerts';
// import { useUnreadCounts } from '../../hooks/useUnreadCounts'; // No longer needed - badges removed from sidebar
import { MenuItemGroup, MenuItemProps } from '../../types';
import { i18n } from '../../utils/i18n';
import { useHasBreakfastService } from '../../views/BreakfastOrders/queries';
import { useUpdateUserLanguage } from '../../views/UserProfile/queries/useUpdateUserLanguage';

// Wrapper component that checks organization flag from state
interface OrganizationWrapperProps {
  children: ReactNode;
}

const OrganizationWrapper: FC<OrganizationWrapperProps> = ({ children }) => {
  const { hasOrganization, isResolved, organizations } = useUserState();

  // Wait for user data to be resolved from Firestore
  if (!isResolved || hasOrganization === null) {
    return (
      <div className="w-full h-[100vh] flex flex-col items-center justify-center">
        <DashboardLoadingIcon />
      </div>
    );
  }

  // If no active organization (associated_organization), redirect based on organizations array
  if (hasOrganization === false) {
    const hasOrganizationsList = !!(organizations && Array.isArray(organizations) && organizations.length > 0);

    if (hasOrganizationsList) {
      return <LocaleNavigate to="/select-organization" replace />;
    }

    return <LocaleNavigate to="/create-organization" replace />;
  }

  // User has active organization - render children (Layout with Outlet)
  return <Fragment key="OrgWrapper">{children}</Fragment>;
};

const isTestExpired = (plan: string | null, testExpirationDate: Timestamp | null): boolean => {
  if (plan !== 'TEST') return false;
  if (!testExpirationDate) return false;
  return testExpirationDate.toMillis() <= Date.now();
};

export const ProtectedRoute: FC = () => {
  const navigate = useLocaleNavigate();
  const location = useLocation();
  const { currentUser, isLoading, userLanguage } = useUserState();
  const { organizationId, languages: orgLanguages } = useOrganizationState();
  const {
    featuresApp, enabledIntegrations, stripePaymentsEnabled, plan, testExpirationDate,
    isResolved: orgResolved, isDisabled,
  } = useOrganizationState();
  const { t } = useTranslation();
  const { language } = useLanguageState();
  // Decyduje o linku „Śniadania" w grupie zamówień — jeden lekki odczyt na sesję.
  const hasBreakfastService = useHasBreakfastService(organizationId);
  // Badge counts removed from sidebar - no longer needed
  // const {
  //   newReportsCount,
  //   newOrdersCount,
  //   newShoppingOrdersCount,
  //   newReservationsCount,
  // } = useUnreadCounts(currentUser?.uid, organizationId);
  const { showError, showSuccess } = useHeroToast();

  // Dźwięk i powiadomienia systemowe — jedno źródło: dokumenty w `users/{uid}/notifications`.
  useNotificationAlerts(currentUser?.uid, organizationId, userLanguage || 'pl');

  // State for language selection modals
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showInitialLanguageModal, setShowInitialLanguageModal] = useState(false);

  // Use refs to track if we've already checked/shown modal for this org
  const hasShownModalRef = useRef<string | null>(null);
  const isCheckingRef = useRef(false);
  const previousOrgIdRef = useRef<string | null>(null);

  // Mutation for updating user language
  const { mutate: updateLanguage, isPending: isUpdatingLanguage } = useUpdateUserLanguage({
    onSuccess: () => {
      showSuccess(t('common.updateSuccess'));
      setShowLanguageModal(false);
      setShowInitialLanguageModal(false);
      isCheckingRef.current = false;
      // Don't reset hasShownModalRef - keep it to prevent showing again in the same session
      // It will be reset only when organization changes (see effect below)
    },
    onError: () => {
      showError(t('common.error'));
      setShowLanguageModal(false);
      setShowInitialLanguageModal(false);
      // Mark as shown even on error to prevent repeated prompts
      const checkKey = `${organizationId}-${language}-${userLanguage}`;
      hasShownModalRef.current = checkKey;
    },
  });

  // Synchronize language from localStorage to i18n
  useEffect(() => {
    if (language && language !== i18n.language) {
      i18n.changeLanguage(language);
    }
  }, [language]);

  // Check if user needs to set up their language
  useEffect(() => {
    // Create a unique key for this check
    const checkKey = `${organizationId}-${language}-${userLanguage}`;

    // Skip if already checked for this exact combination
    if (hasShownModalRef.current === checkKey) {
      return;
    }

    // Skip if already checking (prevent race conditions)
    if (isCheckingRef.current) {
      return;
    }

    // Skip if not ready
    if (!orgResolved || !currentUser || !orgLanguages || orgLanguages.length === 0) {
      return;
    }

    // Mark as checking
    isCheckingRef.current = true;

    // Check if user has no language set in database
    const hasNoLanguageSet = !userLanguage || userLanguage === '';

    if (hasNoLanguageSet) {
      if (language && language !== i18n.language) {
        i18n.changeLanguage(language);
      }

      setShowInitialLanguageModal(true);
      hasShownModalRef.current = checkKey;
      isCheckingRef.current = false;
      return;
    }

    // Check the actual current language (from languageState which may be from browser)
    /* `userLanguage` niesie też KONTEKST JĘZYKOWY TREŚCI (gość może mieć treści po
       francusku), więc do języka panelu bierzemy go tylko wtedy, gdy jest jednym z
       języków interfejsu. Inaczej podejrzenie języka treści otwierałoby modal. */
    const fallbackLanguage = isSupportedLocale(userLanguage || '') ? userLanguage : DEFAULT_LOCALE;
    const currentLanguage = language || fallbackLanguage;
    // Chodzi o język PANELU, więc liczy się lista przetłumaczonych języków interfejsu,
    // a nie języki, w których hotel prowadzi treści dla gościa.
    const isLanguageSupported = isSupportedLocale(currentLanguage);

    if (!isLanguageSupported) {
      if (currentLanguage && currentLanguage !== i18n.language) {
        i18n.changeLanguage(currentLanguage);
      }

      setShowLanguageModal(true);
      hasShownModalRef.current = checkKey;
    } else {
      hasShownModalRef.current = checkKey;
    }

    // Reset checking flag
    isCheckingRef.current = false;
    // CRITICAL: DO NOT include showLanguageModal or showInitialLanguageModal in dependencies
    // They will cause infinite re-renders when modal opens
  }, [orgResolved, currentUser, userLanguage, language, orgLanguages, organizationId]);

  // Reset modal check and redirect to dashboard when organization changes
  useEffect(() => {
    // Skip on first render (when previousOrgIdRef is null)
    if (previousOrgIdRef.current !== null && previousOrgIdRef.current !== organizationId) {
      navigate('/', { replace: true });

      // Reset modal states
      hasShownModalRef.current = null;
      setShowLanguageModal(false);
      setShowInitialLanguageModal(false);
    }

    // Update the previous organization ID
    previousOrgIdRef.current = organizationId;
  }, [organizationId, navigate]);

  // Handle modal close (when user dismisses without selecting)
  const handleModalClose = () => {
    // Mark this combination as checked to prevent showing again
    const checkKey = `${organizationId}-${language}-${userLanguage}`;
    hasShownModalRef.current = checkKey;

    // Close modals
    setShowLanguageModal(false);
    setShowInitialLanguageModal(false);
  };

  // Handle language selection
  const handleLanguageSelect = (selectedLanguage: string) => {
    if (currentUser?.uid) {
      // Immediately change i18n to selected language for better UX
      i18n.changeLanguage(selectedLanguage);

      // Immediately close modals
      setShowLanguageModal(false);
      setShowInitialLanguageModal(false);

      // Optimistically update userState to prevent modal from reopening
      // before Firestore listener updates it
      useUserState.setState({ userLanguage: selectedLanguage });

      // Also update languageState for consistency
      useLanguageState.getState().setLanguage(selectedLanguage);

      // Update hasShownModalRef to mark as checked for the new language
      const newCheckKey = `${organizationId}-${selectedLanguage}-${selectedLanguage}`;
      hasShownModalRef.current = newCheckKey;

      // Update language in database
      updateLanguage({
        userId: currentUser.uid,
        language: selectedLanguage,
      });
    }
  };

  const menuConfig = useMemo<MenuItemProps[]>(() => {
    const items = [...getTranslatedMenuConfig(t)];

    // Lista śniadaniowa dokładana do grupy zamówień — tylko gdy hotel prowadzi
    // serwis śniadaniowy, inaczej link prowadziłby na pusty widok.
    if (hasBreakfastService) {
      const commerce = items.find(
        (item): item is MenuItemGroup => item.kind === 'group' && item.path === '/orders',
      );
      if (commerce) {
        commerce.subItem = [...commerce.subItem, getBreakfastOrdersEntry(t)];
      }
    }

    // Rejestr płatności ma sens dopiero, gdy obiekt w ogóle przyjmuje płatności online.
    // Bez tego zakładka prowadzi do tabeli, która nigdy nie będzie miała ani jednego
    // wiersza, a hotelarz szuka w niej powodu, dla którego jest pusta.
    /* `kind: 'group'`, nie `'item'`: Płatności rozwijają się na Transakcje i Zwroty.
       Po tej zmianie stary warunek szukał pozycji, której już nie ma, i zakładka
       zostawała w menu także przy wyłączonych płatnościach. */
    const payments = items.findIndex((item) => item.kind !== 'divider' && item.path === '/payments');
    if (!stripePaymentsEnabled && payments !== -1) items.splice(payments, 1);

    // Sekcja PMS tuż pod Dashboardem — recepcja zagląda tu częściej niż w treści hotelu.
    if (enabledIntegrations.includes('opera')) {
      items.splice(1, 0, ...getTranslatedOperaEntries(t));
    }

    // Add External Applications section if organization has MUSEUM feature
    if (featuresApp && featuresApp.includes('MUSEUM')) {
      items.push(...getTranslatedExternalAppsEntries(t));
    }

    return items;
  }, [featuresApp, enabledIntegrations, stripePaymentsEnabled, hasBreakfastService, organizationId, t]);

  if (isLoading || !orgResolved) {
    return (
      <div className="w-full h-[100vh] flex flex-col items-center justify-center">
        <DashboardLoadingIcon size="sm" />
      </div>
    );
  }

  if (!currentUser) return <LocaleNavigate to="/login" replace />;

  // Check email verification for new users (created after 2026-02-22)
  if (currentUser && currentUser.metadata) {
    const createdAt = new Date(currentUser.metadata.creationTime || 0).getTime();
    const implementationDate = new Date('2026-02-22').getTime();
    const shouldCheckVerification = createdAt >= implementationDate;

    if (shouldCheckVerification && !currentUser.emailVerified) {
      return <LocaleNavigate to="/verify-email" state={{ email: currentUser.email }} replace />;
    }
  }

  // Subscription check - wait until plan is loaded from Firestore
  const planUnknown = plan === null;
  const isTestPlan = plan === 'TEST';
  const testExpired = isTestPlan && isTestExpired(plan, testExpirationDate);
  const allowedPaths = ['/settings', '/my-profile', '/support-service'];
  const isAllowedPath = allowedPaths.some((p) => location.pathname.includes(p));
  const showExpiredContent = testExpired && !isAllowedPath;

  // Wyłączona organizacja: nawigacja zostaje (user widzi, gdzie jest i może się
  // przełączyć na inną organizację), ale treść widoku zastępuje komunikat.
  // Wyjątki to ekrany „o użytkowniku", nie o hotelu — inaczej właściciel
  // wyłączonego obiektu nie dostałby się nawet do własnego profilu.
  const disabledAllowedPaths = ['/my-profile', '/select-organization'];
  const isDisabledAllowedPath = disabledAllowedPaths.some((p) => location.pathname.includes(p));
  const showDisabledContent = isDisabled && !isDisabledAllowedPath;

  const layoutKey = `layout-${organizationId}-${Array.isArray(featuresApp) ? featuresApp.slice().sort().join('_') : ''}-${enabledIntegrations.slice().sort().join('_')}-${stripePaymentsEnabled ? 'pay' : 'nopay'}`;

  return (
    <>
      {/* Modal for initial language setup when user has no language set */}
      <ModalInitialLanguageSetup
        isOpen={showInitialLanguageModal}
        onClose={handleModalClose}
        onConfirm={handleLanguageSelect}
        availableLanguages={[...SUPPORTED_LOCALES]}
        defaultLanguage={DEFAULT_LOCALE}
        isLoading={isUpdatingLanguage}
      />

      {/* Modal for changing language when current language is not supported */}
      <ModalSelectLanguage
        isOpen={showLanguageModal}
        onClose={handleModalClose}
        onConfirm={handleLanguageSelect}
        availableLanguages={[...SUPPORTED_LOCALES]}
        defaultLanguage={DEFAULT_LOCALE}
        isLoading={isUpdatingLanguage}
        isDismissable={false}
      />

      <OrganizationWrapper>
        <Layout
          key={layoutKey}
          menuConfig={menuConfig}
        >
          <ErrorBoundary resetKey={location.pathname}>
            {planUnknown ? (
              <div className="w-full flex items-center justify-center min-h-[400px]">
                <DashboardLoadingIcon size="sm" />
              </div>
            ) : showDisabledContent ? (
              <OrganizationDisabled />
            ) : showExpiredContent ? (
              <TrialExpired />
            ) : (
              /* Granica Suspense stoi TU, a nie nad routerem: leniwo ładowana trasa
                 zawiesza się przy pierwszym wejściu, a najbliższa granica decyduje, co
                 zniknie z ekranu na ten czas. Nad routerem znikał cały panel razem z
                 sidebarem i topbarem, więc klik w menu wyglądał jak przeładowanie
                 aplikacji. Tutaj gaśnie wyłącznie obszar treści. */
              <Suspense fallback={(
                <div className="w-full flex items-center justify-center min-h-[400px]">
                  <DashboardLoadingIcon size="sm" />
                </div>
              )}
              >
                <Outlet />
              </Suspense>
            )}
          </ErrorBoundary>
        </Layout>
      </OrganizationWrapper>
    </>
  );
};
